// src/components/ui/CustomDateTimePicker.tsx
// System Design Glassmorphic Calendar & Time Picker Popover with Local Time Formatting & Validation.
import { useState, useRef, useEffect, useMemo } from 'react'
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Check,
  AlertCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'

interface CustomDateTimePickerProps {
  value: string // ISO string or datetime-local format 'YYYY-MM-DDTHH:mm'
  onChange: (value: string) => void
  minDate?: Date | null
  maxDate?: Date | null
  placeholder?: string
  disabled?: boolean
  className?: string
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/** Formats a Date object into local 'YYYY-MM-DDTHH:mm' string without UTC timezone shift */
function formatLocalDateTime(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const mins = String(d.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${mins}`
}

export default function CustomDateTimePicker({
  value,
  onChange,
  minDate = null,
  maxDate = null,
  placeholder = 'Select date & time…',
  disabled = false,
  className = '',
}: CustomDateTimePickerProps) {
  const [open, setOpen] = useState(false)
  const [computedPlacement, setComputedPlacement] = useState<'top' | 'bottom'>('bottom')
  const ref = useRef<HTMLDivElement>(null)

  // Smart placement based on available viewport space
  useEffect(() => {
    if (!open) return
    const updatePlacement = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        const spaceAbove = rect.top
        if (spaceBelow < 380 && spaceAbove > 320) {
          setComputedPlacement('top')
        } else {
          setComputedPlacement('bottom')
        }
      }
    }
    updatePlacement()
    window.addEventListener('resize', updatePlacement)
    window.addEventListener('scroll', updatePlacement, true)
    return () => {
      window.removeEventListener('resize', updatePlacement)
      window.removeEventListener('scroll', updatePlacement, true)
    }
  }, [open])

  // Parse initial selected date/time
  const selectedDate = useMemo(() => {
    if (!value) return null
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }, [value])

  // Determine initial view focus:
  // 1. selectedDate if valid
  // 2. maxDate if maxDate < Date.now() (so we don't open on a month where all dates are blocked)
  // 3. minDate if minDate > Date.now()
  // 4. Current date
  const initialViewDate = useMemo(() => {
    if (selectedDate) return selectedDate
    if (maxDate && maxDate.getTime() < Date.now()) return maxDate
    if (minDate && minDate.getTime() > Date.now()) return minDate
    return new Date()
  }, [selectedDate, maxDate, minDate])

  // Calendar view month & year state
  const [viewYear, setViewYear] = useState<number>(() => initialViewDate.getFullYear())
  const [viewMonth, setViewMonth] = useState<number>(() => initialViewDate.getMonth())

  // 12-Hour Time State (1-12 Hour, 0-59 Minute, AM/PM)
  const [hour12, setHour12] = useState<number>(() => {
    if (!selectedDate) return 12
    const h = selectedDate.getHours()
    return h % 12 === 0 ? 12 : h % 12
  })
  const [minute, setMinute] = useState<number>(() => (selectedDate ? selectedDate.getMinutes() : 0))
  const [ampm, setAmPm] = useState<'AM' | 'PM'>(() => {
    if (!selectedDate) return 'PM'
    return selectedDate.getHours() >= 12 ? 'PM' : 'AM'
  })

  // Convert 12h + AM/PM to 24h
  const hour24 = useMemo(() => {
    let h = hour12
    if (ampm === 'PM' && h < 12) h += 12
    if (ampm === 'AM' && h === 12) h = 0
    return h
  }, [hour12, ampm])

  // Sync internal state when selectedDate changes or when opening on a restricted maxDate
  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear())
      setViewMonth(selectedDate.getMonth())
      const h = selectedDate.getHours()
      setHour12(h % 12 === 0 ? 12 : h % 12)
      setMinute(selectedDate.getMinutes())
      setAmPm(h >= 12 ? 'PM' : 'AM')
    } else if (maxDate && maxDate.getTime() < Date.now()) {
      setViewYear(maxDate.getFullYear())
      setViewMonth(maxDate.getMonth())
    }
  }, [selectedDate, maxDate])

  // Outside click listener
  useEffect(() => {
    if (disabled) {
      setOpen(false)
      return
    }
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [disabled])

  const handlePrevYear = () => {
    setViewYear((y) => y - 1)
  }

  const handleNextYear = () => {
    setViewYear((y) => y + 1)
  }

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear((y) => y - 1)
    } else {
      setViewMonth((m) => m - 1)
    }
  }

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear((y) => y + 1)
    } else {
      setViewMonth((m) => m + 1)
    }
  }

  // Calendar grid calculations
  const gridCells = useMemo(() => {
    const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay()
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()

    const startLimit = minDate ? new Date(minDate) : null
    if (startLimit) startLimit.setHours(0, 0, 0, 0)

    const endLimit = maxDate ? new Date(maxDate) : null

    const cells: Array<{ day: number; date: Date; isDisabled: boolean }> = []

    for (let day = 1; day <= daysInMonth; day++) {
      const cellDateStart = new Date(viewYear, viewMonth, day, 0, 0, 0)
      const cellDateEnd = new Date(viewYear, viewMonth, day, 23, 59, 59)
      const isBeforeMin = startLimit ? cellDateEnd.getTime() < startLimit.getTime() : false
      const isAfterMax = endLimit ? cellDateStart.getTime() > endLimit.getTime() : false
      const isDisabled = isBeforeMin || isAfterMax
      cells.push({ day, date: new Date(viewYear, viewMonth, day), isDisabled })
    }

    return { firstDayIndex, cells }
  }, [viewYear, viewMonth, minDate, maxDate])

  // Validation: Check if the currently constructed date + time violates constraints
  const timeValidationError = useMemo(() => {
    const activeDate = selectedDate || new Date(viewYear, viewMonth, new Date().getDate())
    const fullConstructed = new Date(
      activeDate.getFullYear(),
      activeDate.getMonth(),
      activeDate.getDate(),
      hour24,
      minute
    )

    if (minDate && fullConstructed.getTime() < minDate.getTime()) {
      return 'Selected time is before the allowed minimum constraint'
    }
    if (maxDate && fullConstructed.getTime() > maxDate.getTime()) {
      return 'Selected time exceeds the allowed maximum constraint'
    }
    return null
  }, [selectedDate, viewYear, viewMonth, hour24, minute, minDate, maxDate])

  const handleSelectDay = (day: number) => {
    const newDate = new Date(viewYear, viewMonth, day, hour24, minute)
    onChange(formatLocalDateTime(newDate))
  }

  const commitTimeChange = (newH12: number, newMin: number, newAmPm: 'AM' | 'PM') => {
    setHour12(newH12)
    setMinute(newMin)
    setAmPm(newAmPm)

    let h24 = newH12
    if (newAmPm === 'PM' && h24 < 12) h24 += 12
    if (newAmPm === 'AM' && h24 === 12) h24 = 0

    const baseDate = selectedDate || new Date(viewYear, viewMonth, new Date().getDate())
    const newDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h24, newMin)
    onChange(formatLocalDateTime(newDate))
  }

  const formattedDisplay = useMemo(() => {
    if (!selectedDate) return ''
    return selectedDate.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }, [selectedDate])

  return (
    <div className={`relative w-full ${className}`} ref={ref}>
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={`
          w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl
          border border-[var(--field-brd)] bg-[var(--field)] text-xs text-left
          font-semibold text-[var(--txt)] transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-[var(--mint-500)]/25 focus:border-[var(--mint-500)]
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[var(--mint-500)]/40 cursor-pointer'}
        `}
      >
        <span className="flex items-center gap-2 truncate">
          <CalendarIcon size={15} className="text-[var(--mint-500)] shrink-0" />
          <span className={formattedDisplay ? 'text-[var(--txt)] font-extrabold' : 'text-[var(--txt-3)] font-medium'}>
            {formattedDisplay || placeholder}
          </span>
        </span>
        {value ? (
          <X
            size={14}
            className="text-[var(--txt-3)] hover:text-[var(--rose)] transition-colors shrink-0 ml-1 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
            }}
          />
        ) : (
          <Clock size={14} className="text-[var(--txt-3)] shrink-0 ml-1" />
        )}
      </button>

      {/* Popover Calendar & Time Picker */}
      {open && !disabled && (
        <div
          className={`
            absolute left-0 right-0 p-4 bg-[var(--bg-2)] border border-[var(--glass-brd)]
            rounded-2xl shadow-2xl z-[1050] min-w-[300px] backdrop-blur-md
            animate-in fade-in zoom-in-95 duration-150 space-y-4
            ${computedPlacement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
          `}
        >
          {/* Calendar Month & Year Header */}
          <div className="flex items-center justify-between pb-1">
            <span className="text-xs font-extrabold text-[var(--txt)] flex items-center gap-1.5">
              <span>{MONTH_NAMES[viewMonth]}</span>
              <span className="text-[var(--mint-500)] font-mono">{viewYear}</span>
            </span>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={handlePrevYear}
                title="Previous Year"
                className="p-1 rounded-lg text-[var(--txt-3)] hover:text-[var(--txt)] hover:bg-[var(--glass-brd)] transition-colors cursor-pointer"
              >
                <ChevronsLeft size={15} />
              </button>
              <button
                type="button"
                onClick={handlePrevMonth}
                title="Previous Month"
                className="p-1 rounded-lg text-[var(--txt-3)] hover:text-[var(--txt)] hover:bg-[var(--glass-brd)] transition-colors cursor-pointer"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                title="Next Month"
                className="p-1 rounded-lg text-[var(--txt-3)] hover:text-[var(--txt)] hover:bg-[var(--glass-brd)] transition-colors cursor-pointer"
              >
                <ChevronRight size={15} />
              </button>
              <button
                type="button"
                onClick={handleNextYear}
                title="Next Year"
                className="p-1 rounded-lg text-[var(--txt-3)] hover:text-[var(--txt)] hover:bg-[var(--glass-brd)] transition-colors cursor-pointer"
              >
                <ChevronsRight size={15} />
              </button>
            </div>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {DAYS_OF_WEEK.map((d) => (
              <span key={d} className="text-[10px] font-bold text-[var(--txt-3)] uppercase">
                {d}
              </span>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Blank offset cells */}
            {Array.from({ length: gridCells.firstDayIndex }).map((_, i) => (
              <div key={`empty-${i}`} className="w-8 h-8" />
            ))}

            {/* Month Day Buttons */}
            {gridCells.cells.map((cell) => {
              const isSelected =
                selectedDate &&
                selectedDate.getFullYear() === viewYear &&
                selectedDate.getMonth() === viewMonth &&
                selectedDate.getDate() === cell.day

              const isToday =
                new Date().getFullYear() === viewYear &&
                new Date().getMonth() === viewMonth &&
                new Date().getDate() === cell.day

              return (
                <button
                  key={cell.day}
                  type="button"
                  disabled={cell.isDisabled}
                  onClick={() => handleSelectDay(cell.day)}
                  className={`
                    w-8 h-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center cursor-pointer
                    ${
                      isSelected
                        ? 'bg-gradient-to-r from-[#48d6a2] to-[#22b083] text-[#06231a] font-extrabold shadow-sm'
                        : isToday
                        ? 'border border-[var(--mint-500)] text-[var(--mint-500)]'
                        : cell.isDisabled
                        ? 'text-gray-400 dark:text-gray-600 opacity-25 cursor-not-allowed pointer-events-none bg-transparent line-through'
                        : 'text-[var(--txt)] hover:bg-[var(--glass-brd)]'
                    }
                  `}
                >
                  {cell.day}
                </button>
              )
            })}
          </div>

          {/* Custom System Design Time Picker Controls */}
          <div className="pt-3 border-t border-[var(--glass-brd)] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-[var(--txt-3)] flex items-center gap-1">
                <Clock size={13} className="text-[var(--mint-500)]" /> Select Time
              </span>

              {/* AM/PM Toggle Pill */}
              <div className="flex items-center p-0.5 rounded-lg bg-[var(--field)] border border-[var(--field-brd)]">
                <button
                  type="button"
                  onClick={() => commitTimeChange(hour12, minute, 'AM')}
                  className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md transition-all cursor-pointer ${
                    ampm === 'AM' ? 'bg-[var(--mint-500)] text-[#06231a]' : 'text-[var(--txt-3)]'
                  }`}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={() => commitTimeChange(hour12, minute, 'PM')}
                  className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md transition-all cursor-pointer ${
                    ampm === 'PM' ? 'bg-[var(--mint-500)] text-[#06231a]' : 'text-[var(--txt-3)]'
                  }`}
                >
                  PM
                </button>
              </div>
            </div>

            {/* Steppers Row for Hours & Minutes */}
            <div className="flex items-center justify-center gap-3 py-1">
              {/* Hour Control */}
              <div className="flex items-center gap-1 bg-[var(--field)] border border-[var(--field-brd)] px-2.5 py-1.5 rounded-xl">
                <input
                  type="text"
                  inputMode="numeric"
                  value={String(hour12).padStart(2, '0')}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value.replace(/\D/g, ''), 10)
                    if (!isNaN(parsed)) {
                      const clamped = Math.max(1, Math.min(12, parsed))
                      commitTimeChange(clamped, minute, ampm)
                    }
                  }}
                  className="text-sm font-black text-[var(--txt)] w-6 text-center bg-transparent border-none outline-none p-0 focus:outline-none"
                />
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => commitTimeChange(hour12 >= 12 ? 1 : hour12 + 1, minute, ampm)}
                    className="p-0.5 rounded hover:bg-[var(--glass-brd)] text-[var(--txt-3)] hover:text-[var(--txt)] cursor-pointer"
                  >
                    <ChevronUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => commitTimeChange(hour12 <= 1 ? 12 : hour12 - 1, minute, ampm)}
                    className="p-0.5 rounded hover:bg-[var(--glass-brd)] text-[var(--txt-3)] hover:text-[var(--txt)] cursor-pointer"
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>
              </div>

              <span className="text-base font-black text-[var(--txt-3)]">:</span>

              {/* Minute Control */}
              <div className="flex items-center gap-1 bg-[var(--field)] border border-[var(--field-brd)] px-2.5 py-1.5 rounded-xl">
                <input
                  type="text"
                  inputMode="numeric"
                  value={String(minute).padStart(2, '0')}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value.replace(/\D/g, ''), 10)
                    if (!isNaN(parsed)) {
                      const clamped = Math.max(0, Math.min(59, parsed))
                      commitTimeChange(hour12, clamped, ampm)
                    }
                  }}
                  className="text-sm font-black text-[var(--txt)] w-6 text-center bg-transparent border-none outline-none p-0 focus:outline-none"
                />
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => commitTimeChange(hour12, (minute + 5) % 60, ampm)}
                    className="p-0.5 rounded hover:bg-[var(--glass-brd)] text-[var(--txt-3)] hover:text-[var(--txt)] cursor-pointer"
                  >
                    <ChevronUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => commitTimeChange(hour12, (minute - 5 + 60) % 60, ampm)}
                    className="p-0.5 rounded hover:bg-[var(--glass-brd)] text-[var(--txt-3)] hover:text-[var(--txt)] cursor-pointer"
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* Validation Error Warning Badge */}
            {timeValidationError && (
              <div className="flex items-center gap-1.5 p-2 rounded-xl bg-[var(--rose)]/10 text-[var(--rose)] border border-[var(--rose)]/20 text-[10.5px] font-bold">
                <AlertCircle size={13} className="shrink-0" />
                <span>{timeValidationError}</span>
              </div>
            )}
          </div>

          {/* Confirm Button */}
          <button
            type="button"
            disabled={!!timeValidationError}
            onClick={() => !timeValidationError && setOpen(false)}
            className={`
              w-full py-2.5 px-3 rounded-xl text-xs font-extrabold flex items-center justify-center gap-1.5 transition-all shadow-sm
              ${
                timeValidationError
                  ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#48d6a2] to-[#22b083] text-[#06231a] hover:brightness-105 active:scale-95 cursor-pointer'
              }
            `}
          >
            <Check size={15} className="stroke-[2.5]" />
            <span>Apply Date & Time</span>
          </button>
        </div>
      )}
    </div>
  )
}
